import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import {
  ChevronLeft as PreviousIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  CalendarViewWeek as WeekViewIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Person as DriverIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  List as ListIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { fetchDrivers } from '../../redux/slices/driversSlice';
import { fetchVehicles, startVehiclesStatusListener } from '../../redux/slices/vehiclesSlice';
import { fetchRides } from '../../redux/slices/ridesSlice';
import { fetchPlannedShifts } from '../../redux/slices/plannedShiftsSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { onSnapshot, collection, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useWaitTimes } from '../../hooks/useWaitTimeManager';

// Dayjs auf Deutsch einstellen
dayjs.locale('de');

// Hilfsfunktion für aktive Schicht im neuen Array-System
const getActiveShift = (driverData) => {
  if (!driverData || !driverData.shifts || !Array.isArray(driverData.shifts)) {
    return null;
  }
  return driverData.shifts.find(shift => shift.isActive) || null;
};

// Styled Components für das Gantt-Diagramm
const GanttContainer = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2]
}));

const GanttHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default
}));

const ViewToggleGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '600px',
  overflow: 'auto',
  backgroundColor: theme.palette.background.paper
}));

const VehicleList = styled(Box)(({ theme }) => ({
  width: '250px',
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  overflow: 'auto'
}));

const VehicleRow = styled(Box)(({ theme, isActive }) => ({
  height: '60px',
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  backgroundColor: isActive ? theme.palette.action.selected : 'inherit',
  '&:hover': {
    backgroundColor: theme.palette.action.hover
  }
}));

const TimeGrid = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  overflow: 'auto'
}));

const TimeHeader = styled(Box)(({ theme }) => ({
  height: '50px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  backgroundColor: theme.palette.background.default,
  position: 'sticky',
  top: 0,
  zIndex: 2
}));

const TimeSlot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCurrentTime' && prop !== 'slotWidth',
})(({ theme, isCurrentTime, slotWidth }) => ({
  minWidth: `${slotWidth}px`,
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: isCurrentTime ? theme.palette.primary.light : 'inherit',
  position: 'relative'
}));

const VehicleTimelineRow = styled(Box)(({ theme }) => ({
  height: '60px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  position: 'relative'
}));

const CurrentTimeLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '2px',
  backgroundColor: theme.palette.error.main,
  zIndex: 10,
  opacity: 0.8
}));

const ActivityBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDelayed' && prop !== 'isPending' && prop !== 'type' && prop !== 'orderType' && prop !== 'reason',
})(({ theme, type, isDelayed, isPending, orderType, reason }) => {
  const getColor = () => {
    if (isDelayed) return theme.palette.error.main;
    
    switch (type) {
      case 'ride':
        // Unterscheide zwischen Sofort- und Vorbestellungen
        if (orderType === 2) {
          // Sofortbestellungen: Immer grün (egal welcher Status)
          return theme.palette.success.main; // Grün für Sofortbestellungen
        } else if (orderType === 1) {
          // Vorbestellungen: Immer blau (egal welcher Status)
          return theme.palette.info.main; // Blau für Vorbestellungen
        } else {
          // Fallback für Fahrten ohne orderType
          return isPending ? theme.palette.warning.main : theme.palette.error.main;
        }
      case 'break':
      case 'pause':
        return theme.palette.warning.main; // Orange für Pausen
      case 'maintenance':
        return theme.palette.grey[500]; // Grau für Wartung
      case 'offline':
        // Unterschiedliche Farben je nach Offline-Grund
        switch (reason) {
          case 'manual_logout':
            return '#ff5722'; // Rot für manuellen Logout
          case 'app_closed':
            return '#ff9800'; // Orange für App-Schließung  
          case 'connection_lost':
            return '#795548'; // Braun für Verbindungsverlust
          default:
            return '#ff5722'; // Standard rot für unbekannte Gründe
        }
      case 'reconnect':
        return '#4caf50'; // Grün für Wiederverbindung
      case 'planned_shift':
        return '#ffd700'; // Gelb für geplante Schichten
      default:
        return theme.palette.primary.main;
    }
  };

  return {
    position: 'absolute',
    height: '30px',
    top: '15px',
    backgroundColor: getColor(),
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    color: theme.palette.common.white,
    fontSize: '0.75rem',
    fontWeight: 500,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    border: isDelayed ? `2px solid ${theme.palette.error.dark}` : 'none',
    boxShadow: theme.shadows[1],
    '&:hover': {
      opacity: 0.8,
      transform: 'translateY(-1px)',
      boxShadow: theme.shadows[3]
    }
  };
});

const ApproachTimeBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'orderType',
})(({ theme, orderType }) => {
  const getBaseColor = () => {
    if (orderType === 2) {
      return theme.palette.success.main; // Grün für Sofortbestellungen
    } else if (orderType === 1) {
      return theme.palette.info.main; // Blau für Vorbestellungen
    } else {
      return theme.palette.primary.main;
    }
  };

  const baseColor = getBaseColor();
  
  return {
    position: 'absolute',
    height: '30px',
    top: '15px',
    backgroundColor: 'transparent',
    backgroundImage: `repeating-linear-gradient(
      45deg,
      ${baseColor}33,
      ${baseColor}33 4px,
      transparent 4px,
      transparent 8px
    )`,
    border: `1px dashed ${baseColor}`,
    borderRadius: '4px 0 0 4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    color: baseColor,
    fontSize: '0.7rem',
    fontWeight: 500,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    '&:hover': {
      opacity: 0.8,
      transform: 'translateY(-1px)',
      backgroundColor: `${baseColor}11`
    }
  };
});

const ShiftStartMarker = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '3px',
  height: '100%',
  backgroundColor: theme.palette.info.main,
  top: 0,
  zIndex: 5,
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    width: '7px',
    height: '7px',
    backgroundColor: theme.palette.info.main,
    borderRadius: '50%'
  },
  '&:hover': {
    backgroundColor: theme.palette.info.dark,
    '&::before': {
      backgroundColor: theme.palette.info.dark
    }
  }
}));

const ShiftEndMarker = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '3px',
  height: '100%',
  backgroundColor: theme.palette.error.main,
  top: 0,
  zIndex: 5,
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    width: '7px',
    height: '7px',
    backgroundColor: theme.palette.error.main,
    borderRadius: '50%'
  },
  '&:hover': {
    backgroundColor: theme.palette.error.dark,
    '&::before': {
      backgroundColor: theme.palette.error.dark
    }
  }
}));

const FleetUtilizationRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  minHeight: '80px'
}));

const UtilizationLabel = styled(Box)(({ theme }) => ({
  width: '250px',
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center'
}));

const UtilizationChart = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  position: 'relative',
  height: '20px',
  overflow: 'hidden'
}));

const UtilizationTimeline = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  height: '20px',
  display: 'flex',
  backgroundColor: theme.palette.grey[200]
}));

const UtilizationSegment = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const getBackgroundColor = () => {
    switch (status) {
      case 'normal':
        return theme.palette.success.main; // Grün - normal verfügbar
      case 'overtime':
        return theme.palette.warning.main; // Gelb - nur Überstunden verfügbar
      case 'planned':
        return '#ffd700'; // Gelb - geplante Schichten
      case 'unavailable':
      default:
        return theme.palette.error.main; // Rot - nicht verfügbar
    }
  };

  return {
    position: 'absolute',
    height: '100%',
    backgroundColor: getBackgroundColor(),
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.8,
      boxShadow: theme.shadows[2]
    }
  };
});

const ScheduledRideBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})(({ theme, color }) => ({
  position: 'absolute',
  height: '100%',
  backgroundColor: color || theme.palette.grey[900],
  opacity: 0.7,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.grey[700]}`,
  '&:hover': {
    opacity: 0.9,
    boxShadow: theme.shadows[2]
  }
}));

const WeekOverviewContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const WeekGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(8, 1fr)',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2)
}));

const WeekCell = styled(Box)(({ theme, isHeader, utilization = 0 }) => {
  const getBackgroundColor = () => {
    if (isHeader) return theme.palette.background.default;
    
    if (utilization < 0.3) return theme.palette.success.light;
    if (utilization < 0.7) return theme.palette.warning.light;
    return theme.palette.error.light;
  };

  return {
    padding: theme.spacing(1),
    textAlign: 'center',
    backgroundColor: getBackgroundColor(),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    cursor: isHeader ? 'default' : 'pointer',
    fontWeight: isHeader ? 'bold' : 'normal',
    '&:hover': !isHeader ? {
      backgroundColor: theme.palette.action.hover
    } : {}
  };
});

const ShiftCalendar = () => {
  const dispatch = useDispatch();
  
  // Redux state
  const { vehicles, isLoading: vehiclesLoading, error: vehiclesError } = useSelector(state => state.vehicles);
  const { isLoading: ridesLoading, error: ridesError } = useSelector(state => state.rides);
  const { drivers, isLoading: driversLoading } = useSelector(state => state.drivers);
  const { plannedShifts, isLoading: plannedShiftsLoading } = useSelector(state => state.plannedShifts);
  
  // Global WaitTime Hook (nur für Anzeige, keine Updates mehr)
  const { waitTimes: globalWaitTimes, nextUnavailable: globalNextUnavailable } = useWaitTimes();
  
  // Local state
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('gantt'); // 'gantt', 'week'
  const [showFullDay, setShowFullDay] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [zoomLevel, setZoomLevel] = useState('12h'); // '3h', '6h', '12h'
  const [activities, setActivities] = useState([]);
  const [immediateRideActivities, setImmediateRideActivities] = useState([]);
  const [scheduledRideActivities, setScheduledRideActivities] = useState([]);
  const [breakActivities, setBreakActivities] = useState([]);
  const [offlineActivities, setOfflineActivities] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [shiftsData, setShiftsData] = useState(null);
  const [scheduledRides, setScheduledRides] = useState([]);
  const [plannedShiftActivities, setPlannedShiftActivities] = useState([]);
  
  // Ref to hold current vehicles for use in listeners
  const vehiclesRef = useRef(vehicles);
  vehiclesRef.current = vehicles;
  
  // Ref to accumulate activities from multiple listeners
  const immediateActivitiesRef = useRef(new Map());
  
  // Consolidate all activities whenever individual activity types change
  useEffect(() => {
    const consolidated = [...immediateRideActivities, ...scheduledRideActivities, ...breakActivities, ...offlineActivities];
    // Note: plannedShiftActivities are now shown in fleet utilization timeline instead
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Konsolidiere Aktivitäten:", {
        immediate: immediateRideActivities.length,
        scheduled: scheduledRideActivities.length, 
        breaks: breakActivities.length,
        offline: offlineActivities.length,
        plannedShifts: plannedShiftActivities.length,
        total: consolidated.length
      });
    }
    setActivities(consolidated);
  }, [immediateRideActivities, scheduledRideActivities, breakActivities, offlineActivities]);

  // WaitTimeManager Updates werden jetzt von AppDataManager global gesteuert
  // Dieser Effect ist nicht mehr nötig
  
  // Realtime data setup
  useEffect(() => {
    const loadInitialData = async () => {
      console.log("ShiftCalendar: Lade initiale Daten...");
      
      try {
        // Initiale Daten laden
        await dispatch(fetchVehicles());
        await dispatch(fetchRides({ status: ['scheduled', 'pre_accepted', 'open', 'accepted', 'arrived', 'in_progress', 'completed', 'cancelled', 'cancelled_by_driver'] }));
        await dispatch(fetchDrivers());
        
        // Geplante Schichten für das aktuelle Datum laden
        const dateStr = currentDate.toISOString().split('T')[0];
        await dispatch(fetchPlannedShifts({ 
          startDate: dateStr, 
          endDate: dateStr 
        }));
        
        console.log("ShiftCalendar: Initiale Daten erfolgreich geladen!");
        
        // Nach dem ersten Laden den Echtzeit-Listener für Fahrzeuge starten
        console.log("ShiftCalendar: Starte Echtzeit-Listener für Fahrzeug-Status...");
        setTimeout(() => {
          dispatch(startVehiclesStatusListener());
          console.log("ShiftCalendar: Echtzeit-Listener gestartet");
        }, 1000);
      } catch (error) {
        console.error("ShiftCalendar: Fehler beim Laden der initialen Daten:", error);
      }
    };
    
    loadInitialData();
    
    // Update current time every 2 minutes to reduce re-renders
    const timeInterval = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
      
      // Automatischer Datumswechsel um Mitternacht
      const isToday = currentDate.toDateString() === now.toDateString();
      if (!isToday && currentDate < now) {
        // Wenn currentDate in der Vergangenheit liegt und es ein neuer Tag ist, 
        // wechsle automatisch zum aktuellen Tag
        console.log("ShiftCalendar: Automatischer Datumswechsel erkannt, wechsle zu:", now.toDateString());
        setCurrentDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
      }
    }, 120000);
    
    return () => {
      clearInterval(timeInterval);
    };
  }, [dispatch, currentDate]);

  // Initial load of shifts data for current date and previous date (for rolling view)
  useEffect(() => {
    const formatDateString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const currentDateString = formatDateString(currentDate);
    const previousDate = new Date(currentDate);
    previousDate.setDate(previousDate.getDate() - 1);
    const previousDateString = formatDateString(previousDate);
    
    console.log("ShiftCalendar: Lade initiale Shift-Daten für:", currentDateString, "und", previousDateString);
    
    // Initial load of shifts data for both current and previous day
    const loadInitialShifts = async () => {
      try {
        const currentShiftsDocRef = doc(db, 'shifts', currentDateString);
        const previousShiftsDocRef = doc(db, 'shifts', previousDateString);
        
        const [currentDoc, previousDoc] = await Promise.all([
          getDoc(currentShiftsDocRef),
          getDoc(previousShiftsDocRef)
        ]);
        
        // Kombiniere Shifts-Daten
        const combinedData = { drivers: {} };
        
        // Füge Previous Day Shifts hinzu
        if (previousDoc.exists() && previousDoc.data().drivers) {
          Object.entries(previousDoc.data().drivers).forEach(([driverId, driverData]) => {
            combinedData.drivers[driverId] = { ...driverData, _fromPreviousDay: true };
          });
        }
        
        // Füge Current Day Shifts hinzu (überschreibt bei Konflikten)
        if (currentDoc.exists() && currentDoc.data().drivers) {
          Object.entries(currentDoc.data().drivers).forEach(([driverId, driverData]) => {
            combinedData.drivers[driverId] = { ...driverData, _fromCurrentDay: true };
          });
        }
        
        setShiftsData(combinedData);
        console.log("ShiftCalendar: Initiale Shift-Daten geladen (kombiniert)");
      } catch (error) {
        console.error("ShiftCalendar: Fehler beim Laden der initialen Shift-Daten:", error);
      }
    };
    
    loadInitialShifts();
  }, [currentDate]);

  // Load break activities when shiftsData and vehicles are available
  useEffect(() => {
    if (!shiftsData) {
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Lade Pausen-Aktivitäten...", { 
        hasShiftsData: !!shiftsData,
        vehiclesCount: vehiclesRef.current ? vehiclesRef.current.length : 0 
      });
    }
    
    const newBreakActivities = [];
    
    if (shiftsData.drivers) {
      Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
        const currentShift = getActiveShift(driverData);
        if (currentShift && currentShift.breaks && Array.isArray(currentShift.breaks)) {
          currentShift.breaks.forEach((breakItem) => {
            if (breakItem.startTime) {
              const startTime = breakItem.startTime.toDate();
              const endTime = breakItem.endTime 
                ? breakItem.endTime.toDate() 
                : new Date(startTime.getTime() + ((breakItem.plannedDuration || 30) * 60000));
              
              // Find vehicle for this driver - allow breaks even if no vehicle assigned
              const driverVehicle = vehiclesRef.current ? 
                vehiclesRef.current.find(v => v.current_driver?.id === driverId) : null;
              
              // Always create break activity, even if no vehicle found
              const now = new Date();
              const calculatedStatus = (() => {
                if (breakItem.endTime) {
                  return endTime <= now ? 'completed' : 'active';
                } else {
                  return endTime <= now ? 'completed' : 'active';
                }
              })();
              
              newBreakActivities.push({
                id: `break-${driverId}-${startTime.getTime()}`,
                type: 'break',
                vehicleId: driverVehicle ? driverVehicle.id : `virtual-${driverId}`, // Virtual ID if no vehicle
                startTime,
                endTime,
                endTimePlanned: endTime,
                destination: breakItem.type || 'Pause',
                driverId,
                status: calculatedStatus,
                isDelayed: false,
                plannedDuration: breakItem.plannedDuration
              });
            }
          });
        }
      });
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Gefundene Pausen-Aktivitäten:", newBreakActivities.length);
    }
    setBreakActivities(newBreakActivities);
  }, [shiftsData, vehicles]); // Add vehicles back as dependency for breaks

  // Offline-Perioden verarbeiten (ähnlich wie Pausen)
  useEffect(() => {
    if (!shiftsData) return;
    
    const newOfflineActivities = [];
    
    if (shiftsData.drivers) {
      Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
        // Prüfe sowohl neue Array-Struktur als auch alte Struktur für Rückwärtskompatibilität
        let shiftsArray = [];
        
        if (driverData.shifts && Array.isArray(driverData.shifts)) {
          // Neue Array-Struktur
          shiftsArray = driverData.shifts;
        } else if (driverData.shiftStart) {
          // Alte Struktur - migriere zu Array-Format für Verarbeitung
          shiftsArray = [{
            shiftNumber: 1,
            shiftStart: driverData.shiftStart,
            shiftEnd: driverData.shiftEnd,
            isActive: driverData.isActive || false,
            breaks: driverData.breaks || [],
            offlinePeriods: [] // Alte Struktur hat keine Offline-Perioden
          }];
        }
        
        // Verarbeite alle Schichten
        shiftsArray.forEach((shift) => {
          if (shift.offlinePeriods && Array.isArray(shift.offlinePeriods)) {
            shift.offlinePeriods.forEach((offlinePeriod) => {
              if (offlinePeriod.offlineStart) {
                const startTime = offlinePeriod.offlineStart.toDate();
                const endTime = offlinePeriod.offlineEnd 
                  ? offlinePeriod.offlineEnd.toDate() 
                  : new Date(); // Wenn noch offline, bis jetzt
                
                // Finde Fahrzeug für diesen Fahrer
                const driverVehicle = vehiclesRef.current ? 
                  vehiclesRef.current.find(v => v.current_driver?.id === driverId) : null;
                
                const now = new Date();
                const calculatedStatus = offlinePeriod.offlineEnd ? 'completed' : 'active';
                
                newOfflineActivities.push({
                  id: `offline-${driverId}-${startTime.getTime()}`,
                  type: 'offline',
                  vehicleId: driverVehicle ? driverVehicle.id : `virtual-${driverId}`,
                  startTime,
                  endTime,
                  endTimePlanned: endTime,
                  destination: `Offline (${offlinePeriod.reason || 'unbekannt'})`,
                  driverId,
                  status: calculatedStatus,
                  isDelayed: false,
                  reason: offlinePeriod.reason || 'manual_logout',
                  offlineReason: offlinePeriod.reason || 'manual_logout'
                });
              }
            });
          }
        });
      });
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Gefundene Offline-Aktivitäten:", newOfflineActivities.length);
    }
    setOfflineActivities(newOfflineActivities);
  }, [shiftsData, vehicles]);

  // Convert planned shifts to activities for timeline visualization
  useEffect(() => {
    if (!plannedShifts || plannedShifts.length === 0) {
      setPlannedShiftActivities([]);
      return;
    }

    const newPlannedShiftActivities = [];
    const currentDateString = currentDate.toISOString().split('T')[0];

    plannedShifts.forEach(shift => {
      if (shift.status === 'cancelled') return;

      // Check if this shift includes the current date
      const relevantDates = shift.dates.filter(date => date === currentDateString);
      
      relevantDates.forEach(date => {
        const [hours, minutes] = shift.startTime.split(':').map(Number);
        const startTime = new Date(currentDate);
        startTime.setHours(hours, minutes, 0, 0);
        
        const endTime = new Date(startTime);
        endTime.setHours(startTime.getHours() + (shift.plannedDuration || 8));

        // Find vehicle for this shift
        let vehicleId = shift.vehicleId;
        if (!vehicleId && vehicles && vehicles.length > 0) {
          // If no specific vehicle assigned, try to find the driver's current vehicle
          const driverVehicle = vehicles.find(v => v.current_driver?.id === shift.driverId);
          vehicleId = driverVehicle?.id || `virtual-planned-${shift.driverId}`;
        } else if (!vehicleId) {
          vehicleId = `virtual-planned-${shift.driverId}`;
        }

        newPlannedShiftActivities.push({
          id: `planned-shift-${shift.id}-${date}`,
          type: 'planned_shift',
          vehicleId,
          startTime,
          endTime,
          endTimePlanned: endTime,
          destination: `Geplante Schicht`,
          driverId: shift.driverId,
          driverName: shift.driverName || shift.driver?.name || 'Unbekannter Fahrer',
          status: 'planned',
          shiftId: shift.id,
          notes: shift.notes,
          plannedDuration: shift.plannedDuration
        });
      });
    });

    if (process.env.NODE_ENV === 'development') {
      console.debug("ShiftCalendar: Gefundene geplante Schichten:", newPlannedShiftActivities.length);
    }
    
    setPlannedShiftActivities(newPlannedShiftActivities);
  }, [plannedShifts, currentDate, vehicles]);

  // Setup realtime listeners for rides and shifts
  useEffect(() => {
    // Don't block listeners if vehicles are empty - they can load rides independently
    
    const formatDateString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const currentDateString = formatDateString(currentDate);
    
    // Listen to shifts data for timeline calculation
    // Lade Shifts sowohl für den aktuellen Tag als auch den Vortag (für rollierende Ansicht)
    const shiftsDocRef = doc(db, 'shifts', currentDateString);
    
    // Berechne Vortag
    const previousDate = new Date(currentDate);
    previousDate.setDate(previousDate.getDate() - 1);
    const previousDateString = formatDateString(previousDate);
    const previousShiftsDocRef = doc(db, 'shifts', previousDateString);
    
    let currentShiftsData = null;
    let previousShiftsData = null;
    
    const updateCombinedShiftsData = () => {
      // Kombiniere Shifts-Daten vom aktuellen Tag und Vortag
      const combinedData = { drivers: {} };
      
      // Füge Previous Day Shifts hinzu
      if (previousShiftsData && previousShiftsData.drivers) {
        Object.entries(previousShiftsData.drivers).forEach(([driverId, driverData]) => {
          combinedData.drivers[driverId] = { ...driverData, _fromPreviousDay: true };
        });
      }
      
      // Füge Current Day Shifts hinzu (überschreibt bei Konflikten)
      if (currentShiftsData && currentShiftsData.drivers) {
        Object.entries(currentShiftsData.drivers).forEach(([driverId, driverData]) => {
          combinedData.drivers[driverId] = { ...driverData, _fromCurrentDay: true };
        });
      }
      
      setShiftsData(combinedData);
    };
    
    const unsubscribeCurrentShifts = onSnapshot(shiftsDocRef, (docSnapshot) => {
      currentShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
      updateCombinedShiftsData();
    });
    
    const unsubscribePreviousShifts = onSnapshot(previousShiftsDocRef, (docSnapshot) => {
      previousShiftsData = docSnapshot.exists() ? docSnapshot.data() : null;
      updateCombinedShiftsData();
    });
    
    // Listen to rides for the selected date - both immediate orders (acceptedTimestamp) and general (timestamp)
    // Erweitere den Zeitbereich um den Vortag für die rollierende Ansicht
    const isToday = currentDate.toDateString() === new Date().toDateString();
    const shouldShowFullDay = showFullDay || !isToday;
    
    let queryStartTime, queryEndTime;
    
    if (shouldShowFullDay) {
      // Volltagsansicht: nur aktueller Tag
      queryStartTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
      queryEndTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
    } else {
      // Rollierende Ansicht: erweitere um Vortag wenn nötig
      const currentHour = new Date().getHours();
      if (currentHour < 1) {
        // Wenn es vor 01:00 ist, beginne um 22:00 des Vortags
        queryStartTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() - 1, 22, 0, 0);
      } else {
        // Sonst beginne um (currentHour - 1) des aktuellen Tages
        queryStartTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), currentHour - 1, 0, 0);
      }
      
      // Ende: 12 Stunden nach aktuellem Zeitpunkt
      const endHour = currentHour + 12;
      if (endHour >= 24) {
        queryEndTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1, endHour - 24, 0, 0);
      } else {
        queryEndTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate(), endHour, 0, 0);
      }
    }
    
    const startOfDay = queryStartTime;
    const endOfDay = queryEndTime;
    
    // Query for immediate orders with acceptedTimestamp
    const immediateRidesQuery = query(
      collection(db, 'rides'),
      where('acceptedTimestamp', '>=', startOfDay),
      where('acceptedTimestamp', '<', endOfDay),
      where('status', 'in', ['accepted', 'arrived', 'in_progress', 'completed', 'cancelled', 'cancelled_by_driver'])
    );
    
    // Query for other rides with timestamp
    const generalRidesQuery = query(
      collection(db, 'rides'),
      where('timestamp', '>=', startOfDay),
      where('timestamp', '<', endOfDay)
    );
    
    // Listen to all scheduled rides for the selected date (pre-bookings with scheduledTime)
    // Erweitere den Zeitraum für die Query wenn wir im 12-Stunden-Fenster sind
    
    let queryStartDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    let queryEndDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
    
    // Wenn wir heute sind und nicht im Volltagsmodus, erweitere die Query auf die nächsten 12 Stunden
    if (isToday && !shouldShowFullDay) {
      const currentHour = new Date().getHours();
      // Wenn nach 12 Uhr mittags, includiere auch den nächsten Tag
      if (currentHour >= 12) {
        queryEndDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 2);
      }
    }
    
    const scheduledRidesWithTimeQuery = query(
      collection(db, 'rides'),
      where('status', 'in', ['scheduled', 'pre_accepted', 'accepted', 'arrived', 'in_progress', 'completed', 'cancelled', 'cancelled_by_driver']),
      where('scheduledTime', '>=', queryStartDate),
      where('scheduledTime', '<', queryEndDate)
    );
    
    // Combined query for all unassigned rides (scheduled and open) for timeline
    // Verwende den gleichen erweiterten Zeitraum wie für scheduled rides
    const unassignedRidesQuery = query(
      collection(db, 'rides'),
      where('status', 'in', ['scheduled', 'open'])
    );
    
    const unsubscribeUnassignedRides = onSnapshot(
      unassignedRidesQuery, 
      (snapshot) => {
        try {
          const allUnassignedRides = [];
          
          snapshot.forEach((doc) => {
            try {
              const rideData = doc.data();
              const isToday = (timeField) => {
                if (!timeField) return false;
                const rideDate = timeField.toDate();
                const today = currentDate;
                return rideDate.toDateString() === today.toDateString();
              };
              
              // Check if ride is for current date or within the extended time window
              const isScheduledInWindow = rideData.scheduledTime && 
                rideData.scheduledTime.toDate() >= queryStartDate && 
                rideData.scheduledTime.toDate() < queryEndDate;
              const isOpenInWindow = rideData.timestamp && 
                rideData.timestamp.toDate() >= queryStartDate && 
                rideData.timestamp.toDate() < queryEndDate;
              
              if ((isScheduledInWindow || isOpenInWindow) && rideData.estimatedTimeMinutes) {
                const startTime = rideData.scheduledTime ? 
                  rideData.scheduledTime.toDate() : 
                  rideData.timestamp.toDate();
                const endTime = new Date(startTime.getTime() + (rideData.estimatedTimeMinutes * 60000));
                
                allUnassignedRides.push({
                  id: doc.id,
                  startTime,
                  endTime,
                  destination: rideData.destination || 'Unbekanntes Ziel',
                  customerName: rideData.customerName,
                  vehicleType: rideData.vehicleType,
                  status: rideData.status
                });
              }
            } catch (error) {
              console.error('Error processing unassigned ride document:', error);
            }
          });
          
          setScheduledRides(allUnassignedRides);
        } catch (error) {
          console.error('Error in unassigned rides listener:', error);
        }
      },
      (error) => {
        console.error('Error listening to unassigned rides:', error);
      }
    );
    
    // Combined handler for both immediate and general rides
    const handleRidesSnapshot = (snapshot, queryType) => {
      snapshot.forEach((doc) => {
        const rideData = doc.data();
        
        // Für Sofortbestellungen mit acceptedTimestamp
        if (rideData.acceptedTimestamp && rideData.driverId && rideData.estimatedTimeMinutes) {
          const startTime = rideData.acceptedTimestamp.toDate();
          const endTime = new Date(startTime.getTime() + (rideData.estimatedTimeMinutes * 60000));
          
          // Finde das Fahrzeug des Fahrers wenn nicht direkt verfügbar
          let vehicleId = rideData.vehicleId;
          if (!vehicleId && rideData.driverId && vehiclesRef.current) {
            const driverVehicle = vehiclesRef.current.find(v => v.current_driver?.id === rideData.driverId);
            vehicleId = driverVehicle?.id;
          }
          
          // Create activity even if no vehicle found (use virtual ID)
          const activity = {
            id: doc.id,
            type: 'ride',
            vehicleId: vehicleId || `virtual-ride-${rideData.driverId}`,
            startTime,
            endTime: rideData.completedTimestamp ? rideData.completedTimestamp.toDate() : endTime,
            endTimePlanned: endTime,
            destination: rideData.destination || 'Unbekanntes Ziel',
            driverId: rideData.driverId,
            status: rideData.status,
            isDelayed: rideData.status !== 'completed' && new Date() > endTime,
            customerName: rideData.customerName,
            orderType: rideData.orderType,
            estimatedTimeMinutes: rideData.estimatedTimeMinutes // Fahrtdauer hinzufügen
          };
          
          immediateActivitiesRef.current.set(doc.id, activity);
        }
      });
      
      // Update immediate ride activities with all accumulated activities
      const allActivities = Array.from(immediateActivitiesRef.current.values());
      setImmediateRideActivities(allActivities);
    };
    
    // Clear existing activities for this date
    immediateActivitiesRef.current.clear();
    
    // Set up listeners for both queries
    const unsubscribeImmediateRides = onSnapshot(immediateRidesQuery, (snapshot) => 
      handleRidesSnapshot(snapshot, 'immediate')
    );
    
    const unsubscribeGeneralRides = onSnapshot(generalRidesQuery, (snapshot) => 
      handleRidesSnapshot(snapshot, 'general')
    );
    
    // Listen to all scheduled rides (pre-bookings with scheduledTime)
    const unsubscribeScheduledRidesWithTime = onSnapshot(scheduledRidesWithTimeQuery, (snapshot) => {
      const scheduledActivities = [];
      
      snapshot.forEach((doc) => {
        const rideData = doc.data();
        
        if (rideData.scheduledTime && rideData.driverId && rideData.estimatedTimeMinutes) {
          // Überprüfe, ob die Fahrt im aktuell sichtbaren Zeitfenster liegt
          const scheduledTime = rideData.scheduledTime.toDate();
          
          // Berechne das sichtbare Zeitfenster basierend auf aktueller Ansicht
          const currentHour = new Date().getHours();
          const isCurrentlyToday = currentDate.toDateString() === new Date().toDateString();
          const shouldShowFullDayNow = showFullDay || !isCurrentlyToday;
          
          let windowStart, windowEnd;
          
          if (shouldShowFullDayNow) {
            // Volltagsansicht: 00:00 - 23:59
            windowStart = new Date(currentDate);
            windowStart.setHours(0, 0, 0, 0);
            
            windowEnd = new Date(currentDate);
            windowEnd.setHours(23, 59, 59, 999);
          } else {
            // Rollierende Ansicht: 1 Stunde zurück + 12 Stunden voraus
            const startHour = Math.max(0, currentHour - 1);
            const endHour = startHour + 13; // 13 Stunden Fenster
            
            windowStart = new Date(currentDate);
            windowStart.setHours(startHour, 0, 0, 0);
            
            windowEnd = new Date(currentDate);
            if (endHour >= 24) {
              // Über Mitternacht hinaus
              windowEnd.setDate(windowEnd.getDate() + 1);
              windowEnd.setHours(endHour - 24, 0, 0, 0);
            } else {
              windowEnd.setHours(endHour, 0, 0, 0);
            }
          }
          
          // Nur Fahrten anzeigen, die im sichtbaren Zeitfenster beginnen
          if (scheduledTime < windowStart || scheduledTime >= windowEnd) {
            return; // Skip this ride
          }
          
          // Prüfe ob Anfahrtszeit benötigt wird (nur bei pre_accepted Status)
          const needsApproachTime = rideData.status === 'pre_accepted';
          const approachTimeMinutes = 10; // 10 Minuten Anfahrtszeit
          
          // Berechne Startzeit basierend auf Status
          let actualStartTime, approachStartTime, mainStartTime;
          
          if (rideData.startedAt) {
            // Fahrt bereits gestartet - verwende tatsächliche Startzeit
            actualStartTime = rideData.startedAt.toDate();
            mainStartTime = scheduledTime;
            approachStartTime = needsApproachTime ? 
              new Date(scheduledTime.getTime() - (approachTimeMinutes * 60000)) : actualStartTime;
          } else if (needsApproachTime) {
            // Pre-accepted: Anfahrtszeit einplanen
            approachStartTime = new Date(scheduledTime.getTime() - (approachTimeMinutes * 60000));
            mainStartTime = scheduledTime;
            actualStartTime = approachStartTime; // Aktivität beginnt mit Anfahrt
          } else {
            // Andere Status: wie bisher
            actualStartTime = scheduledTime;
            mainStartTime = scheduledTime;
            approachStartTime = null;
          }
          
          const endTime = new Date(mainStartTime.getTime() + (rideData.estimatedTimeMinutes * 60000));
          
          // Finde das Fahrzeug des Fahrers
          const driverVehicle = vehiclesRef.current ? 
            vehiclesRef.current.find(v => v.current_driver?.id === rideData.driverId) : null;
          
          // Create activity even if no vehicle found (use virtual ID)
          scheduledActivities.push({
            id: doc.id,
            type: 'ride',
            vehicleId: driverVehicle ? driverVehicle.id : `virtual-scheduled-${rideData.driverId}`,
            startTime: actualStartTime, // Gesamtstart (inklusive Anfahrt)
            endTime: rideData.completedTimestamp ? rideData.completedTimestamp.toDate() : endTime,
            endTimePlanned: endTime,
            destination: rideData.destination || 'Unbekanntes Ziel',
            driverId: rideData.driverId,
            status: rideData.status,
            isDelayed: !['completed', 'cancelled', 'cancelled_by_driver'].includes(rideData.status) && new Date() > endTime,
            customerName: rideData.customerName,
            orderType: rideData.orderType,
            estimatedTimeMinutes: rideData.estimatedTimeMinutes, // Fahrtdauer hinzufügen
            // Neue Eigenschaften für Anfahrtszeit
            hasApproachTime: needsApproachTime,
            approachStartTime: approachStartTime,
            mainStartTime: mainStartTime,
            approachTimeMinutes: needsApproachTime ? approachTimeMinutes : 0
          });
        }
      });
      
      // Update scheduled ride activities directly (consolidated in useEffect above)
      setScheduledRideActivities(scheduledActivities);
    });
    
    // Note: Break activities are now handled in separate useEffect above for better performance
    
    return () => {
      unsubscribeImmediateRides();
      unsubscribeGeneralRides();
      unsubscribeScheduledRidesWithTime();
      unsubscribeUnassignedRides();
      unsubscribeCurrentShifts();
      unsubscribePreviousShifts();
    };
  }, [currentDate, showFullDay]); // showFullDay hinzugefügt, damit Listener bei Änderung neu aufgebaut werden

  // Helper functions
  const formatTime = (date) => {
    if (!date) return '--:--';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  // Helper function to get human-readable offline reason text
  const getOfflineReasonText = (reason) => {
    switch (reason) {
      case 'manual_logout':
        return 'Manual abgemeldet';
      case 'app_closed':
        return 'App geschlossen';
      case 'connection_lost':
        return 'Verbindung verloren';
      default:
        return 'Unbekannter Grund';
    }
  };

  // Helper function to calculate and format offline duration
  const calculateOfflineDuration = (startTime, endTime) => {
    const end = endTime || new Date(); // If still offline, use current time
    const durationMs = end - startTime;
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    
    if (durationMinutes < 60) {
      return `${durationMinutes} Min`;
    } else {
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
    }
  };

  const sortVehiclesByActivity = useCallback((vehicles, activities) => {
    return [...vehicles].sort((a, b) => {
      const aActivities = activities.filter(act => act.vehicleId === a.id);
      const bActivities = activities.filter(act => act.vehicleId === b.id);
      
      const getActivityPriority = (vehicle, vehicleActivities) => {
        // Aktive Fahrt (Status: in_progress, accepted, arrived)
        if (vehicleActivities.some(act => act.type === 'ride' && ['in_progress', 'accepted', 'arrived'].includes(act.status))) return 1;
        
        // Geplante Fahrt (alle Scheduled-Status)  
        if (vehicleActivities.some(act => act.type === 'ride' && ['scheduled', 'pre_accepted', 'accepted', 'arrived'].includes(act.status))) return 2;
        
        // In Pause
        if (vehicleActivities.some(act => act.type === 'break' && act.status === 'active')) return 3;
        
        // Verfügbar mit Fahrer
        if (vehicle.current_driver) return 4;
        
        // Ohne Fahrer
        return 5;
      };
      
      return getActivityPriority(a, aActivities) - getActivityPriority(b, bActivities);
    });
  }, []);

  // Berechne Slot-Breite basierend auf Zoom-Level
  const getSlotWidth = useCallback(() => {
    switch (zoomLevel) {
      case '3h': return 480; // 480px pro Stunde (4x zoom)
      case '6h': return 240; // 240px pro Stunde (2x zoom)
      case '12h': 
      default: return 120; // 120px pro Stunde (standard)
    }
  }, [zoomLevel]);

  const getTimeSlots = useCallback(() => {
    const slots = [];
    const currentHour = currentTime.getHours();
    const isToday = currentDate.toDateString() === new Date().toDateString();
    const shouldShowFullDay = showFullDay || !isToday;
    
    if (shouldShowFullDay) {
      // Volltagsansicht: 00:00 - 23:00 (für andere Tage oder manuell aktiviert)
      for (let hour = 0; hour < 24; hour++) {
        slots.push({
          hour,
          displayHour: hour,
          time: `${hour.toString().padStart(2, '0')}:00`,
          isCurrentTime: hour === currentHour && isToday,
          date: new Date(currentDate)
        });
      }
    } else {
      // Rollierende Ansicht basierend auf Zoom-Level
      const hoursToShow = parseInt(zoomLevel); // '3h' -> 3, '6h' -> 6, '12h' -> 12
      const startHour = currentHour - 1; // Erlaube negative Werte für Vortag
      const totalHours = hoursToShow + 1; // +1 für die vergangene Stunde
      
      for (let i = 0; i < totalHours; i++) {
        const hour = startHour + i;
        const displayHour = ((hour % 24) + 24) % 24; // Korrekte Modulo für negative Zahlen
        const dayOffset = Math.floor(hour / 24);
        
        // Berechne das korrekte Datum für diesen Slot
        const slotDate = new Date(currentDate);
        slotDate.setDate(slotDate.getDate() + dayOffset);
        
        slots.push({
          hour: displayHour,
          displayHour,
          absoluteHour: hour, // Für Berechnungen über Mitternacht hinaus
          time: `${displayHour.toString().padStart(2, '0')}:00`,
          isCurrentTime: displayHour === currentHour && 
                        slotDate.toDateString() === new Date().toDateString(),
          date: slotDate,
          isNextDay: dayOffset > 0,
          isPreviousDay: dayOffset < 0
        });
      }
    }
    
    return slots;
  }, [currentTime, currentDate, showFullDay, zoomLevel]);

  const calculateActivityPosition = useCallback((activity) => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { left: '0px', width: '2px' };
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Minuten seit dem ersten angezeigten Zeitslot
    // Verwende Math.max(0, ...) nicht, da negative Werte möglich sind (für vergangene Zeiten)
    const startMinutesFromBeginning = (activity.startTime - baseTime) / (1000 * 60);
    const endMinutesFromBeginning = (activity.endTime - baseTime) / (1000 * 60);
    
    // Pixel-basierte Berechnung basierend auf Zoom-Level
    const slotWidth = getSlotWidth();
    const leftPx = (startMinutesFromBeginning / 60) * slotWidth;
    const widthPx = Math.max(2, ((endMinutesFromBeginning - startMinutesFromBeginning) / 60) * slotWidth);
    
    return { left: `${leftPx}px`, width: `${widthPx}px` };
  }, [getTimeSlots]);

  const calculateApproachTimePosition = useCallback((activity) => {
    if (!activity.hasApproachTime || !activity.approachStartTime) {
      return { left: '0px', width: '0px' };
    }
    
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { left: '0px', width: '0px' };
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Position für Anfahrtszeit
    const approachStartMinutes = (activity.approachStartTime - baseTime) / (1000 * 60);
    const mainStartMinutes = (activity.mainStartTime - baseTime) / (1000 * 60);
    
    const slotWidth = getSlotWidth();
    const leftPx = (approachStartMinutes / 60) * slotWidth;
    const widthPx = Math.max(2, ((mainStartMinutes - approachStartMinutes) / 60) * slotWidth);
    
    return { left: `${leftPx}px`, width: `${widthPx}px` };
  }, [getTimeSlots]);

  const calculateMainActivityPosition = useCallback((activity) => {
    if (!activity.hasApproachTime || !activity.mainStartTime) {
      return calculateActivityPosition(activity);
    }
    
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { left: '0px', width: '2px' };
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Position für Hauptfahrt (ohne Anfahrtszeit)
    const mainStartMinutes = (activity.mainStartTime - baseTime) / (1000 * 60);
    const endMinutesFromBeginning = (activity.endTime - baseTime) / (1000 * 60);
    
    const slotWidth = getSlotWidth();
    const leftPx = (mainStartMinutes / 60) * slotWidth;
    const widthPx = Math.max(2, ((endMinutesFromBeginning - mainStartMinutes) / 60) * slotWidth);
    
    return { left: `${leftPx}px`, width: `${widthPx}px` };
  }, [getTimeSlots, calculateActivityPosition]);

  const getCurrentTimePosition = useCallback(() => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return 0;
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Berechne Minuten seit dem ersten angezeigten Zeitslot
    // Entferne Math.max(0, ...) da negative Werte bei vergangenen Zeiten auftreten können
    const minutesFromBeginning = (currentTime - baseTime) / (1000 * 60);
    
    // Pixel-basierte Berechnung basierend auf Zoom-Level
    const slotWidth = getSlotWidth();
    const totalPositionPx = (minutesFromBeginning / 60) * slotWidth;
    
    return totalPositionPx;
  }, [getTimeSlots, currentTime]);

  const getShiftStartMarkers = (vehicle) => {
    if (!vehicle.current_driver || !shiftsData || !shiftsData.drivers) {
      return [];
    }

    const driverId = vehicle.current_driver.id;
    const driverData = shiftsData.drivers[driverId];
    const currentShift = getActiveShift(driverData);
    
    if (!currentShift || !currentShift.shiftStart) {
      return [];
    }

    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return [];
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const shiftStart = currentShift.shiftStart.toDate();
    
    // Prüfe ob der Schichtbeginn im angezeigten Zeitraum liegt
    const lastSlot = timeSlots[timeSlots.length - 1];
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    if (shiftStart < baseTime || shiftStart > endTime) {
      return [];
    }
    
    // Berechne Position
    const minutesFromBeginning = (shiftStart - baseTime) / (1000 * 60);
    const slotWidth = getSlotWidth();
    const leftPx = (minutesFromBeginning / 60) * slotWidth;
    
    return [{
      time: shiftStart,
      position: leftPx,
      driverName: vehicle.current_driver.user?.name || vehicle.current_driver.name,
      vehicleModel: vehicle.model
    }];
  };

  const getShiftEndMarkers = (vehicle) => {
    if (!vehicle.current_driver || !shiftsData || !shiftsData.drivers) {
      return [];
    }

    const driverId = vehicle.current_driver.id;
    const driverData = shiftsData.drivers[driverId];
    const currentShift = getActiveShift(driverData);
    
    if (!currentShift || !currentShift.shiftEnd) {
      return [];
    }

    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return [];
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const shiftEnd = currentShift.shiftEnd.toDate();
    
    // Prüfe ob das Schichtende im angezeigten Zeitraum liegt
    const lastSlot = timeSlots[timeSlots.length - 1];
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    if (shiftEnd < baseTime || shiftEnd > endTime) {
      return [];
    }
    
    // Berechne Position
    const minutesFromBeginning = (shiftEnd - baseTime) / (1000 * 60);
    const slotWidth = getSlotWidth();
    const leftPx = (minutesFromBeginning / 60) * slotWidth;
    
    return [{
      time: shiftEnd,
      position: leftPx,
      driverName: vehicle.current_driver.user?.name || vehicle.current_driver.name,
      vehicleModel: vehicle.model
    }];
  };

  const calculateScheduledRidesTimeline = () => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0 || scheduledRides.length === 0) return [];
    
    const firstSlot = timeSlots[0];
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    // Filtere erst rides mit gültigen Zeiten, dann sortiere
    const validRides = scheduledRides.filter(ride => ride.startTime && ride.endTime);
    const sortedRides = [...validRides].sort((a, b) => a.startTime - b.startTime);
    
    // Erstelle Timeline mit Überlappungen
    const ridesWithPositions = sortedRides.map((ride, rideIndex) => {
      // Berechne Minuten seit dem ersten angezeigten Zeitslot
      // Entferne Math.max(0, ...) da negative Werte bei vergangenen Zeiten auftreten können
      const startMinutesFromBeginning = (ride.startTime - baseTime) / (1000 * 60);
      const endMinutesFromBeginning = (ride.endTime - baseTime) / (1000 * 60);
      
      // Pixel-basierte Berechnung basierend auf Zoom-Level
      const slotWidth = getSlotWidth();
      const leftPx = (startMinutesFromBeginning / 60) * slotWidth;
      const widthPx = Math.max(2, ((endMinutesFromBeginning - startMinutesFromBeginning) / 60) * slotWidth);
      
      // Finde Überlappungen mit vorherigen Fahrten
      let overlaps = false;
      for (let i = 0; i < rideIndex; i++) {
        const otherRide = sortedRides[i];
        if (ride.startTime < otherRide.endTime && ride.endTime > otherRide.startTime) {
          overlaps = true;
          break;
        }
      }
      
      // Wähle Farbe basierend auf Index und Überlappungen
      const colors = ['#424242', '#616161', '#757575', '#9e9e9e', '#bdbdbd'];
      const color = overlaps ? colors[rideIndex % colors.length] : '#000000';
      
      return {
        ...ride,
        left: leftPx,
        width: widthPx,
        color: color,
        overlaps: overlaps
      };
    });
    
    return ridesWithPositions;
  };

  const memoizedTimeSlots = useMemo(() => getTimeSlots(), [getTimeSlots]);

  const availabilityTimeline = useMemo(() => {
    const timeSlots = memoizedTimeSlots;
    if (timeSlots.length === 0) return { segments: [] };
    
    const firstSlot = timeSlots[0];
    const lastSlot = timeSlots[timeSlots.length - 1];
    
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    const totalMinutes = (endTime - baseTime) / (1000 * 60);
    
    // Hole Schichtdaten für das aktuelle Datum
    const formatDateString = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const currentDateString = formatDateString(currentDate);
    
    // Development only logging - removed excessive timeline logging
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Timeline-Berechnung gestartet:`, { timeSlots: timeSlots.length, totalMinutes });
    }
    
    // Erstelle eine Timeline mit minütlicher Auflösung
    const timeline = [];
    
    for (let minute = 0; minute < totalMinutes; minute++) {
      const checkTime = new Date(baseTime.getTime() + minute * 60000);
      
      let availableCount = 0;
      let totalActiveDrivers = 0;
      
      if (shiftsData && shiftsData.drivers) {
        Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
          // Prüfe ob der Fahrer zu dieser Zeit eine aktive Schicht hat
          const currentShift = getActiveShift(driverData);
          if (!currentShift) return;
          
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          // Schicht muss aktiv sein und zur aktuellen Zeit laufen
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) {
            return;
          }
          
          if (shiftEnd && checkTime > shiftEnd) {
            return;
          }
          
          totalActiveDrivers++;
          
          // Prüfe ob der Fahrer in einer Pause ist
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            
            // Prüfe ob die Pause zur aktuellen Zeit aktiv ist
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          // Prüfe aktive Fahrten aus rides-Sammlung für diesen Fahrer
          // Nur verbindlich zugewiesene Fahrten blockieren den Fahrer
          // 'scheduled' und 'open' sind unverbindlich und werden in "Offene Vorbestellungen" verwaltet
          const isOnRide = activities.some(activity => {
            if (activity.type !== 'ride' || activity.driverId !== driverId) {
              return false;
            }
            
            // Nur verbindlich zugewiesene Status blockieren den Fahrer
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) {
              return false;
            }
            
            // Berücksichtige Anfahrtszeit bei pre_accepted Status
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (!isInBreak && !isOnRide) {
            availableCount++;
          }
        });
      }
      
      // Prüfe ob verfügbare Fahrer noch innerhalb ihrer 8-Stunden-Schicht sind
      let availableWithinShiftHours = 0;
      let availableOverShiftHours = 0;
      
      if (shiftsData && shiftsData.drivers) {
        Object.entries(shiftsData.drivers).forEach(([driverId, driverData]) => {
          const currentShift = getActiveShift(driverData);
          if (!currentShift) return;
          
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) {
            return;
          }
          
          if (shiftEnd && checkTime > shiftEnd) {
            return;
          }
          
          // Berechne 8-Stunden-Grenze ab Schichtbeginn
          const maxShiftTime = new Date(shiftStart.getTime() + (8 * 60 * 60 * 1000));
          
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          const isOnRide = activities.some(activity => {
            if (activity.type !== 'ride' || activity.driverId !== driverId) {
              return false;
            }
            
            // Nur verbindlich zugewiesene Status blockieren den Fahrer
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) {
              return false;
            }
            
            // Berücksichtige Anfahrtszeit bei pre_accepted Status
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (!isInBreak && !isOnRide) {
            if (checkTime <= maxShiftTime) {
              availableWithinShiftHours++;
            } else {
              availableOverShiftHours++;
            }
          }
        });
      }
      
      const totalAvailable = availableWithinShiftHours + availableOverShiftHours;
      
      // Check for planned shifts at this time
      let plannedShiftsCount = 0;
      if (plannedShifts && plannedShifts.length > 0) {
        plannedShifts.forEach(shift => {
          if (shift.status === 'cancelled') return;
          
          // Check if this shift includes the current date
          const relevantDates = shift.dates.filter(date => date === currentDateString);
          if (relevantDates.length === 0) return;
          
          const [hours, minutes] = shift.startTime.split(':').map(Number);
          const shiftStart = new Date(currentDate);
          shiftStart.setHours(hours, minutes, 0, 0);
          
          const shiftEnd = new Date(shiftStart);
          shiftEnd.setHours(shiftStart.getHours() + (shift.plannedDuration || 8));
          
          // Check if current time is within planned shift
          if (checkTime >= shiftStart && checkTime < shiftEnd) {
            plannedShiftsCount++;
          }
        });
      }
      
      // Determine status: prioritize planned shifts when available
      let finalStatus;
      if (plannedShiftsCount > 0 && totalAvailable === 0) {
        finalStatus = 'planned'; // Show planned when no actual drivers available
      } else if (availableWithinShiftHours > 0) {
        finalStatus = 'normal';
      } else if (availableOverShiftHours > 0) {
        finalStatus = 'overtime';
      } else {
        finalStatus = 'unavailable';
      }
      
      timeline.push({
        minute,
        time: checkTime,
        available: totalAvailable > 0,
        availableCount: totalAvailable,
        availableWithinShiftHours,
        availableOverShiftHours,
        totalActiveDrivers,
        plannedShiftsCount,
        status: finalStatus
      });
    }
    
    // Gruppiere zusammenhängende Segmente
    const segments = [];
    let currentSegment = null;
    
    timeline.forEach((point) => {
      if (!currentSegment || currentSegment.status !== point.status) {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = {
          startMinute: point.minute,
          endMinute: point.minute + 1,
          available: point.available,
          availableCount: point.availableCount,
          availableWithinShiftHours: point.availableWithinShiftHours,
          availableOverShiftHours: point.availableOverShiftHours,
          totalWithDriver: point.totalActiveDrivers,
          plannedShiftsCount: point.plannedShiftsCount,
          status: point.status,
          startTime: point.time,
          endTime: new Date(point.time.getTime() + 60000)
        };
      } else {
        currentSegment.endMinute = point.minute + 1;
        currentSegment.endTime = new Date(point.time.getTime() + 60000);
        currentSegment.availableCount = point.availableCount;
        currentSegment.availableWithinShiftHours = point.availableWithinShiftHours;
        currentSegment.availableOverShiftHours = point.availableOverShiftHours;
        currentSegment.totalWithDriver = point.totalActiveDrivers;
        currentSegment.plannedShiftsCount = Math.max(currentSegment.plannedShiftsCount || 0, point.plannedShiftsCount);
      }
    });
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Timeline-Berechnung abgeschlossen:`, { segmentsCount: segments.length });
    }
    
    return { segments };
  }, [memoizedTimeSlots, shiftsData, activities, currentDate, plannedShifts]);


  const calculateTimeToNextUnavailability = () => {
    const now = new Date();
    const { segments } = availabilityTimeline;
    
    if (!segments || segments.length === 0) return null;
    
    // Finde den aktuellen Zeitpunkt in der Timeline
    const currentSegment = segments.find(segment => 
      now >= segment.startTime && now < segment.endTime
    );
    
    // Finde das nächste unavailable Segment nach der aktuellen Zeit
    const nextUnavailableSegment = segments.find(segment => 
      segment.status === 'unavailable' && segment.startTime > now
    );
    
    if (!nextUnavailableSegment) return null;
    
    const minutesUntilUnavailable = Math.ceil((nextUnavailableSegment.startTime - now) / (1000 * 60));
    
    return {
      minutesUntil: minutesUntilUnavailable,
      startTime: nextUnavailableSegment.startTime,
      endTime: nextUnavailableSegment.endTime,
      duration: Math.ceil((nextUnavailableSegment.endTime - nextUnavailableSegment.startTime) / (1000 * 60))
    };
  };

  const findNextAvailableSegment = () => {
    const now = new Date();
    const { segments } = availabilityTimeline;
    
    if (!segments || segments.length === 0) return null;
    
    // Finde das nächste verfügbare Segment (normal oder overtime) nach der aktuellen Zeit
    const nextAvailableSegment = segments.find(segment => 
      (segment.status === 'normal' || segment.status === 'overtime') && 
      segment.startTime > now
    );
    
    if (!nextAvailableSegment) return null;
    
    const minutesUntilAvailable = Math.ceil((nextAvailableSegment.startTime - now) / (1000 * 60));
    
    return {
      minutesUntil: minutesUntilAvailable,
      startTime: nextAvailableSegment.startTime,
      availableCount: nextAvailableSegment.availableCount,
      status: nextAvailableSegment.status
    };
  };

  const calculateCurrentWaitTime = () => {
    if (!shiftsData || !shiftsData.drivers) {
      return { general: "20+ Min", kombi: "20+ Min", minibus: "20+ Min" };
    }

    const now = new Date();
    const { segments } = availabilityTimeline;
    
    if (!segments || segments.length === 0) {
      return { general: "20+ Min", kombi: "20+ Min", minibus: "20+ Min" };
    }

    // Finde aktuelles Segment
    const currentSegment = segments.find(segment => 
      now >= segment.startTime && now < segment.endTime
    );

    // Hilfsfunktion für Aufrundung auf 5-Min-Schritte
    const roundToFiveMinutes = (minutes) => Math.ceil(minutes / 5) * 5;

    // Berechne Wartezeit basierend auf Timeline-Daten
    const calculateWaitTimeFromTimeline = (currentAvailable) => {
      if (currentAvailable > 0) {
        // Fahrer aktuell verfügbar
        const nextUnavailable = calculateTimeToNextUnavailability();
        
        if (!nextUnavailable || nextUnavailable.minutesUntil > 20) {
          // Standard-Wartezeit wenn keine baldige Nicht-Verfügbarkeit
          return "5-10 Min";
        } else {
          // Kurz vor rotem Abschnitt - warte bis nach Ausfallzeit
          const totalWaitTime = nextUnavailable.minutesUntil + nextUnavailable.duration;
          const roundedTime = roundToFiveMinutes(totalWaitTime);
          return `${roundedTime}-${roundedTime + 5} Min (nach Ausfallzeit)`;
        }
      } else {
        // Keine Fahrer verfügbar - finde nächsten verfügbaren Zeitpunkt
        const nextAvailable = findNextAvailableSegment();
        
        if (!nextAvailable) {
          return "30+ Min (keine geplante Verfügbarkeit)";
        }
        
        const roundedTime = roundToFiveMinutes(nextAvailable.minutesUntil);
        return `${roundedTime}-${roundedTime + 5} Min`;
      }
    };

    // Berechne für Standard-Fahrzeuge
    const currentAvailable = currentSegment ? currentSegment.availableCount : 0;
    const generalWaitTime = calculateWaitTimeFromTimeline(currentAvailable);

    // Prüfe erst ob Fahrzeuge des Typs existieren und mit Fahrern besetzt sind
    const availableVehicleTypes = vehiclesRef.current.reduce((acc, vehicle) => {
      if (vehicle.current_driver && vehicle.vehicleType) {
        if (vehicle.vehicleType === 2 || vehicle.vehicleType === 3) acc.kombi = true;
        if (vehicle.vehicleType === 3) acc.minibus = true;
      }
      return acc;
    }, { kombi: false, minibus: false });

    // Berechne für Kombi (Typ 2) - nur wenn Fahrzeuge verfügbar
    let kombiWaitTime = "nicht verfügbar";
    if (availableVehicleTypes.kombi) {
      const kombiAvailability = calculateVehicleTypeAvailability(2);
      const kombiSegments = kombiAvailability.segments;
      const currentKombiSegment = kombiSegments.find(segment => 
        now >= segment.startTime && now < segment.endTime
      );
      kombiWaitTime = calculateWaitTimeFromTimeline(
        currentKombiSegment ? currentKombiSegment.availableCount : 0
      );
    }

    // Berechne für Minibus (Typ 3) - nur wenn Fahrzeuge verfügbar
    let minibusWaitTime = "nicht verfügbar";
    if (availableVehicleTypes.minibus) {
      const minibusAvailability = calculateVehicleTypeAvailability(3);
      const minibusSegments = minibusAvailability.segments;
      const currentMinibusSegment = minibusSegments.find(segment => 
        now >= segment.startTime && now < segment.endTime
      );
      minibusWaitTime = calculateWaitTimeFromTimeline(
        currentMinibusSegment ? currentMinibusSegment.availableCount : 0
      );
    }

    return {
      general: generalWaitTime,
      kombi: kombiWaitTime,
      minibus: minibusWaitTime
    };
  };

  const calculateVehicleTypeAvailability = useCallback((vehicleType) => {
    const timeSlots = getTimeSlots();
    if (timeSlots.length === 0) return { segments: [] };
    
    const firstSlot = timeSlots[0];
    const lastSlot = timeSlots[timeSlots.length - 1];
    
    const baseTime = new Date(firstSlot.date);
    baseTime.setHours(firstSlot.displayHour, 0, 0, 0);
    
    const endTime = new Date(lastSlot.date);
    endTime.setHours(lastSlot.displayHour + 1, 0, 0, 0);
    
    const totalMinutes = (endTime - baseTime) / (1000 * 60);
    
    // Filtere Fahrzeuge nach Typ
    // Für Typ 2 (Kombi/6 Sitze): Inkludiere auch Typ 3 (Minibus/8 Sitze)
    const vehiclesOfType = vehicleType === 2 
      ? vehiclesRef.current.filter(v => v.vehicleType === 2 || v.vehicleType === 3)
      : vehiclesRef.current.filter(v => v.vehicleType === vehicleType);
    
    if (process.env.NODE_ENV === 'development') {
      console.debug(`Fahrzeugtyp ${vehicleType} Timeline:`, { vehicleCount: vehiclesOfType.length });
    }
    
    // Erstelle eine Timeline mit minütlicher Auflösung
    const timeline = [];
    
    for (let minute = 0; minute < totalMinutes; minute++) {
      const checkTime = new Date(baseTime.getTime() + minute * 60000);
      
      let availableCount = 0;
      let totalWithDriver = 0;
      let availableType2Count = 0;
      let availableType3Count = 0;
      let availableWithinShiftHours = 0;
      let availableOverShiftHours = 0;
      
      vehiclesOfType.forEach(vehicle => {
        // Prüfe ob das Fahrzeug einen aktiven Fahrer hat
        if (!vehicle.current_driver) {
          return;
        }
        
        const driverId = vehicle.current_driver.id;
        
        // Prüfe ob der Fahrer eine aktive Schicht hat
        if (shiftsData && shiftsData.drivers && shiftsData.drivers[driverId]) {
          const driverData = shiftsData.drivers[driverId];
          const currentShift = getActiveShift(driverData);
          if (!currentShift) return;
          
          const shiftStart = currentShift.shiftStart?.toDate();
          const shiftEnd = currentShift.shiftEnd?.toDate();
          const isShiftActive = currentShift.isActive;
          
          // Schicht muss aktiv sein und zur aktuellen Zeit laufen
          if (!isShiftActive || !shiftStart || checkTime < shiftStart) {
            return;
          }
          
          if (shiftEnd && checkTime > shiftEnd) {
            return;
          }
          
          totalWithDriver++;
          
          // Berechne 8-Stunden-Grenze ab Schichtbeginn
          const maxShiftTime = new Date(shiftStart.getTime() + (8 * 60 * 60 * 1000));
          
          // Prüfe ob der Fahrer in einer Pause ist
          const isInBreak = currentShift.breaks && currentShift.breaks.some(breakItem => {
            if (!breakItem.startTime) return false;
            
            const breakStart = breakItem.startTime.toDate();
            const breakEnd = breakItem.endTime ? breakItem.endTime.toDate() : 
              new Date(breakStart.getTime() + ((breakItem.plannedDuration || 30) * 60000));
            
            return breakStart <= checkTime && breakEnd > checkTime;
          });
          
          // Prüfe aktive Fahrten
          const isOnRide = activities.some(activity => {
            if (activity.type !== 'ride' || activity.vehicleId !== vehicle.id) {
              return false;
            }
            
            // Nur verbindlich zugewiesene Status blockieren das Fahrzeug
            const blockingStatuses = ['pre_accepted', 'accepted', 'arrived', 'in_progress'];
            if (!blockingStatuses.includes(activity.status)) {
              return false;
            }
            
            // Berücksichtige Anfahrtszeit bei pre_accepted Status
            const effectiveStartTime = (activity.status === 'pre_accepted' && activity.hasApproachTime) ? 
              activity.approachStartTime : activity.startTime;
            
            return effectiveStartTime <= checkTime && activity.endTime > checkTime;
          });
          
          if (!isInBreak && !isOnRide) {
            availableCount++;
            if (vehicle.vehicleType === 2) availableType2Count++;
            if (vehicle.vehicleType === 3) availableType3Count++;
            
            if (checkTime <= maxShiftTime) {
              availableWithinShiftHours++;
            } else {
              availableOverShiftHours++;
            }
          }
        }
      });
      
      // Check for planned shifts for this vehicle type at this time
      let plannedShiftsCount = 0;
      if (plannedShifts && plannedShifts.length > 0) {
        const currentDateString = currentDate.toISOString().split('T')[0];
        
        plannedShifts.forEach(shift => {
          if (shift.status === 'cancelled') return;
          
          // Check if this shift includes the current date
          const relevantDates = shift.dates.filter(date => date === currentDateString);
          if (relevantDates.length === 0) return;
          
          // Check if shift has a vehicle of the correct type
          if (shift.vehicleId) {
            const shiftVehicle = vehiclesRef.current.find(v => v.id === shift.vehicleId);
            if (shiftVehicle) {
              const vehicleMatchesType = vehicleType === 2 
                ? (shiftVehicle.vehicleType === 2 || shiftVehicle.vehicleType === 3)
                : shiftVehicle.vehicleType === vehicleType;
              
              if (!vehicleMatchesType) return;
            }
          }
          
          const [hours, minutes] = shift.startTime.split(':').map(Number);
          const shiftStart = new Date(currentDate);
          shiftStart.setHours(hours, minutes, 0, 0);
          
          const shiftEnd = new Date(shiftStart);
          shiftEnd.setHours(shiftStart.getHours() + (shift.plannedDuration || 8));
          
          // Check if current time is within planned shift
          if (checkTime >= shiftStart && checkTime < shiftEnd) {
            plannedShiftsCount++;
          }
        });
      }
      
      // Determine status: prioritize planned shifts when available
      let finalStatus;
      if (plannedShiftsCount > 0 && availableCount === 0) {
        finalStatus = 'planned'; // Show planned when no actual drivers available
      } else if (availableWithinShiftHours > 0) {
        finalStatus = 'normal';
      } else if (availableOverShiftHours > 0) {
        finalStatus = 'overtime';
      } else {
        finalStatus = 'unavailable';
      }
      
      timeline.push({
        minute,
        time: checkTime,
        available: availableCount > 0,
        availableCount,
        totalWithDriver,
        totalVehicles: vehiclesOfType.length,
        availableType2Count,
        availableType3Count,
        availableWithinShiftHours,
        availableOverShiftHours,
        plannedShiftsCount,
        status: finalStatus
      });
    }
    
    // Gruppiere zusammenhängende Segmente
    const segments = [];
    let currentSegment = null;
    
    timeline.forEach((point) => {
      if (!currentSegment || currentSegment.status !== point.status) {
        if (currentSegment) {
          segments.push(currentSegment);
        }
        currentSegment = {
          startMinute: point.minute,
          endMinute: point.minute + 1,
          available: point.available,
          availableCount: point.availableCount,
          totalWithDriver: point.totalWithDriver,
          totalVehicles: point.totalVehicles,
          availableType2Count: point.availableType2Count,
          availableType3Count: point.availableType3Count,
          availableWithinShiftHours: point.availableWithinShiftHours,
          availableOverShiftHours: point.availableOverShiftHours,
          plannedShiftsCount: point.plannedShiftsCount,
          status: point.status,
          startTime: point.time,
          endTime: new Date(point.time.getTime() + 60000)
        };
      } else {
        currentSegment.endMinute = point.minute + 1;
        currentSegment.endTime = new Date(point.time.getTime() + 60000);
        currentSegment.availableCount = point.availableCount;
        currentSegment.totalWithDriver = point.totalWithDriver;
        currentSegment.availableType2Count = point.availableType2Count;
        currentSegment.availableType3Count = point.availableType3Count;
        currentSegment.availableWithinShiftHours = point.availableWithinShiftHours;
        currentSegment.availableOverShiftHours = point.availableOverShiftHours;
        currentSegment.plannedShiftsCount = Math.max(currentSegment.plannedShiftsCount || 0, point.plannedShiftsCount);
      }
    });
    
    if (currentSegment) {
      segments.push(currentSegment);
    }
    
    return { segments };
  }, [getTimeSlots, shiftsData, activities, currentDate, plannedShifts]);

  // Memoized timeline calculations to prevent recalculation on every render
  const kombiAvailability = useMemo(() => calculateVehicleTypeAvailability(2), [calculateVehicleTypeAvailability]);
  const minibusAvailability = useMemo(() => calculateVehicleTypeAvailability(3), [calculateVehicleTypeAvailability]);

  // Navigation handlers
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (currentView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleDateChange = (date) => {
    if (date) {
      setCurrentDate(date.toDate());
    }
  };

  const handleActivityClick = (event, activity) => {
    setSelectedActivity(activity);
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedActivity(null);
  };

  // Week overview data
  const getWeekOverviewData = () => {
    const weekDays = [];
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1); // Monday
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }
    
    return vehiclesRef.current.map(vehicle => {
      const vehicleWeek = weekDays.map(day => {
        // Calculate utilization for this vehicle on this day
        const dayActivities = activities.filter(a => 
          a.vehicleId === vehicle.id && 
          a.startTime.toDateString() === day.toDateString()
        );
        
        const totalMinutes = dayActivities.reduce((sum, activity) => {
          const duration = (activity.endTime - activity.startTime) / (1000 * 60);
          return sum + duration;
        }, 0);
        
        const utilization = Math.min(1, totalMinutes / (8 * 60)); // 8 hour workday
        
        return { day, utilization, hours: Math.round(totalMinutes / 60) };
      });
      
      return { vehicle, week: vehicleWeek };
    });
  };

  // Loading and error states
  const isLoading = vehiclesLoading || ridesLoading || driversLoading;
  const error = vehiclesError || ridesError;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loading message="Kalender wird geladen..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorMessage
          title="Fehler beim Laden des Kalenders"
          message={error}
          onRetry={() => {
            dispatch(fetchVehicles());
            dispatch(fetchRides({ status: ['scheduled', 'pre_accepted', 'open', 'accepted', 'arrived', 'in_progress', 'completed', 'cancelled', 'cancelled_by_driver'] }));
            dispatch(fetchDrivers());
          }}
        />
      </Box>
    );
  }

  // Render Gantt Chart View
  const renderGanttView = () => {
    const timeSlots = memoizedTimeSlots;
    const currentTimePos = getCurrentTimePosition();
    const isToday = currentDate.toDateString() === new Date().toDateString();
    const isAutoFullDay = !isToday;
    
    return (
      <GanttContainer>
        <GanttHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handlePrevious}>
              <PreviousIcon />
            </IconButton>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={dayjs(currentDate)}
                onChange={handleDateChange}
                slotProps={{ textField: { variant: "outlined", size: "small" } }}
              />
            </LocalizationProvider>
            <IconButton onClick={handleNext}>
              <NextIcon />
            </IconButton>
            <Button 
              variant="outlined" 
              startIcon={<TodayIcon />} 
              onClick={handleToday}
              size="small"
            >
              Heute
            </Button>
          </Box>
          
          <Typography variant="h6">
            {currentDate.toLocaleDateString('de-DE', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showFullDay}
                  onChange={(e) => setShowFullDay(e.target.checked)}
                  disabled={isAutoFullDay}
                />
              }
              label={isAutoFullDay ? "Ganzer Tag (automatisch)" : "Ganzer Tag"}
            />
            {!showFullDay && !isAutoFullDay && (
              <ToggleButtonGroup
                value={zoomLevel}
                exclusive
                onChange={(e, newZoom) => newZoom && setZoomLevel(newZoom)}
                size="small"
              >
                <ToggleButton value="12h" aria-label="12 Stunden">
                  <Tooltip title="12 Stunden Ansicht">
                    <span>12h</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="6h" aria-label="6 Stunden">
                  <Tooltip title="6 Stunden Ansicht">
                    <span>6h</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="3h" aria-label="3 Stunden">
                  <Tooltip title="3 Stunden Ansicht">
                    <span>3h</span>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <ViewToggleGroup>
              <Button 
                variant={currentView === 'gantt' ? 'contained' : 'outlined'} 
                startIcon={<TimelineIcon />}
                onClick={() => setCurrentView('gantt')}
                size="small"
              >
                Gantt
              </Button>
              <Button 
                variant={currentView === 'week' ? 'contained' : 'outlined'} 
                startIcon={<WeekViewIcon />}
                onClick={() => setCurrentView('week')}
                size="small"
              >
                Woche
              </Button>
            </ViewToggleGroup>
          </Box>
        </GanttHeader>
        
        <TimelineContainer>
          <VehicleList>
            <Box sx={{ height: '50px', p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" fontWeight="bold">Fahrzeuge</Typography>
            </Box>
            {sortVehiclesByActivity(vehiclesRef.current, activities).map((vehicle) => (
              <VehicleRow key={vehicle.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {vehicle.model || vehicle.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {vehicle.licensePlate}
                    </Typography>
                  </Box>
                  {vehicle.current_driver && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DriverIcon fontSize="small" color="action" />
                      <Typography variant="caption">
                        {vehicle.current_driver.user?.name || vehicle.current_driver.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </VehicleRow>
            ))}
          </VehicleList>
          
          <TimeGrid>
            <TimeHeader style={{ minWidth: `${timeSlots.length * getSlotWidth()}px` }}>
              {timeSlots.map((slot, index) => (
                <TimeSlot key={`${slot.date.getTime()}-${slot.displayHour}`} isCurrentTime={slot.isCurrentTime} slotWidth={getSlotWidth()}>
                  <Typography variant="caption">
                    {slot.time}
                    {slot.isNextDay && (
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: 'text.secondary' }}>
                        +1 Tag
                      </Typography>
                    )}
                    {slot.isPreviousDay && (
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: 'text.secondary' }}>
                        -1 Tag
                      </Typography>
                    )}
                  </Typography>
                </TimeSlot>
              ))}
            </TimeHeader>
            
            {sortVehiclesByActivity(vehiclesRef.current, activities).map((vehicle) => {
              const vehicleActivities = activities.filter(a => 
                a.vehicleId === vehicle.id || 
                (a.type === 'break' && a.vehicleId === `virtual-${vehicle.current_driver?.id}`) ||
                (a.type === 'ride' && a.vehicleId === `virtual-ride-${vehicle.current_driver?.id}`) ||
                (a.type === 'ride' && a.vehicleId === `virtual-scheduled-${vehicle.current_driver?.id}`) ||
                (a.type === 'planned_shift' && a.vehicleId === `virtual-planned-${vehicle.current_driver?.id}`)
              );
              
              return (
                <VehicleTimelineRow key={vehicle.id} style={{ minWidth: `${timeSlots.length * getSlotWidth()}px` }}>
                  {timeSlots.map((slot) => (
                    <TimeSlot key={`${slot.date.getTime()}-${slot.displayHour}`} slotWidth={getSlotWidth()} />
                  ))}
                  
                  {/* Schichtbeginn-Markierungen */}
                  {getShiftStartMarkers(vehicle).map((marker, index) => (
                    <Tooltip
                      key={`shift-start-${index}`}
                      title={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            Schichtbeginn
                          </Typography>
                          <Typography variant="body2">
                            Fahrer: {marker.driverName}
                          </Typography>
                          <Typography variant="body2">
                            Zeit: {formatTime(marker.time)}
                          </Typography>
                          <Typography variant="body2">
                            Fahrzeug: {marker.vehicleModel}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <ShiftStartMarker
                        style={{
                          left: `${marker.position}px`
                        }}
                      />
                    </Tooltip>
                  ))}
                  
                  {/* Schichtende-Markierungen */}
                  {getShiftEndMarkers(vehicle).map((marker, index) => (
                    <Tooltip
                      key={`shift-end-${index}`}
                      title={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            Schichtende
                          </Typography>
                          <Typography variant="body2">
                            Fahrer: {marker.driverName}
                          </Typography>
                          <Typography variant="body2">
                            Zeit: {formatTime(marker.time)}
                          </Typography>
                          <Typography variant="body2">
                            Fahrzeug: {marker.vehicleModel}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <ShiftEndMarker
                        style={{
                          left: `${marker.position}px`
                        }}
                      />
                    </Tooltip>
                  ))}
                  
                  {vehicleActivities.map((activity) => {
                    const endTime = formatTime(activity.endTime);
                    
                    // Separate Tooltips für Anfahrtszeit und Hauptfahrt
                    const approachTooltipContent = (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Anfahrtszeit
                        </Typography>
                        <Typography variant="body2">
                          Ziel: {activity.destination}
                        </Typography>
                        <Typography variant="body2">
                          Anfahrt: {formatTime(activity.approachStartTime)} - {formatTime(activity.mainStartTime)}
                        </Typography>
                        <Typography variant="body2">
                          Dauer: {activity.approachTimeMinutes} Min
                        </Typography>
                        {activity.customerName && (
                          <Typography variant="body2">
                            Kunde: {activity.customerName}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                          Fahrer fährt zum Abholort
                        </Typography>
                      </Box>
                    );

                    const mainTooltipContent = (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {activity.type === 'ride' ? 'Fahrt' : 'Pause'}
                        </Typography>
                        <Typography variant="body2">
                          Ziel: {activity.destination}
                        </Typography>
                        <Typography variant="body2">
                          Abholung: {formatTime(activity.mainStartTime)}
                        </Typography>
                        <Typography variant="body2">
                          Ende: {endTime}
                        </Typography>
                        <Typography variant="body2">
                          Fahrtdauer: {activity.estimatedTimeMinutes || 'unbekannt'} Min
                        </Typography>
                        {activity.customerName && (
                          <Typography variant="body2">
                            Kunde: {activity.customerName}
                          </Typography>
                        )}
                        {activity.plannedDuration && (
                          <Typography variant="body2">
                            Dauer: {activity.plannedDuration} Min
                          </Typography>
                        )}
                      </Box>
                    );

                    // Enhanced tooltip content with offline activity support
                    const normalTooltipContent = (
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {activity.type === 'offline' ? 'Offline-Periode' : 
                           activity.type === 'reconnect' ? 'Wiederverbindung' :
                           activity.type === 'planned_shift' ? 'Geplante Schicht' :
                           activity.type === 'ride' ? 'Fahrt' : 'Pause'}
                        </Typography>
                        
                        {/* Offline activity specific content */}
                        {activity.type === 'offline' && (
                          <>
                            <Typography variant="body2">
                              Grund: {getOfflineReasonText(activity.reason || activity.offlineReason)}
                            </Typography>
                            <Typography variant="body2">
                              Start: {formatTime(activity.startTime)}
                            </Typography>
                            <Typography variant="body2">
                              {activity.endTime && activity.endTime <= new Date() ? 
                                `Ende: ${endTime}` : 
                                'Status: Noch offline'}
                            </Typography>
                            <Typography variant="body2">
                              Dauer: {calculateOfflineDuration(activity.startTime, activity.endTime)}
                            </Typography>
                            {/* Find driver name from vehicles */}
                            {(() => {
                              const driverVehicle = vehiclesRef.current?.find(v => v.current_driver?.id === activity.driverId);
                              const driverName = driverVehicle?.current_driver?.user?.name || driverVehicle?.current_driver?.name;
                              return driverName ? (
                                <Typography variant="body2">
                                  Fahrer: {driverName}
                                </Typography>
                              ) : null;
                            })()}
                          </>
                        )}
                        
                        {/* Reconnect activity specific content */}
                        {activity.type === 'reconnect' && (
                          <>
                            <Typography variant="body2">
                              Wiederverbindung hergestellt
                            </Typography>
                            <Typography variant="body2">
                              Zeit: {formatTime(activity.startTime)}
                            </Typography>
                            {(() => {
                              const driverVehicle = vehiclesRef.current?.find(v => v.current_driver?.id === activity.driverId);
                              const driverName = driverVehicle?.current_driver?.user?.name || driverVehicle?.current_driver?.name;
                              return driverName ? (
                                <Typography variant="body2">
                                  Fahrer: {driverName}
                                </Typography>
                              ) : null;
                            })()}
                          </>
                        )}
                        
                        {/* Planned shift specific content */}
                        {activity.type === 'planned_shift' && (
                          <>
                            <Typography variant="body2">
                              Fahrer: {activity.driverName}
                            </Typography>
                            <Typography variant="body2">
                              Schichtbeginn: {formatTime(activity.startTime)}
                            </Typography>
                            <Typography variant="body2">
                              Schichtende: {formatTime(activity.endTime)}
                            </Typography>
                            <Typography variant="body2">
                              Geplante Dauer: {activity.plannedDuration || 8} Stunden
                            </Typography>
                            {activity.notes && (
                              <Typography variant="body2">
                                Notizen: {activity.notes}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'warning.main' }}>
                              Flottenauslastung geplant
                            </Typography>
                          </>
                        )}
                        
                        {/* Standard content for rides and breaks */}
                        {activity.type !== 'offline' && activity.type !== 'reconnect' && activity.type !== 'planned_shift' && (
                          <>
                            <Typography variant="body2">
                              Ziel: {activity.destination}
                            </Typography>
                            <Typography variant="body2">
                              Start: {formatTime(activity.startTime)}
                            </Typography>
                            <Typography variant="body2">
                              Ende: {endTime}
                            </Typography>
                            {activity.customerName && (
                              <Typography variant="body2">
                                Kunde: {activity.customerName}
                              </Typography>
                            )}
                            {activity.plannedDuration && (
                              <Typography variant="body2">
                                Dauer: {activity.plannedDuration} Min
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    );
                    
                    if (activity.hasApproachTime) {
                      // Render approach time + main activity as separate bars
                      const approachPosition = calculateApproachTimePosition(activity);
                      const mainPosition = calculateMainActivityPosition(activity);
                      
                      return (
                        <React.Fragment key={activity.id}>
                          {/* Anfahrtszeit */}
                          <Tooltip 
                            title={approachTooltipContent}
                            arrow
                            placement="top"
                            enterDelay={200}
                          >
                            <ApproachTimeBar
                              orderType={activity.orderType}
                              style={approachPosition}
                              onClick={(e) => handleActivityClick(e, activity)}
                            >
                              Anfahrt
                            </ApproachTimeBar>
                          </Tooltip>
                          
                          {/* Hauptfahrt */}
                          <Tooltip 
                            title={mainTooltipContent}
                            arrow
                            placement="top"
                            enterDelay={200}
                          >
                            <ActivityBar
                              type={activity.type}
                              isDelayed={activity.isDelayed}
                              isPending={['scheduled', 'pre_accepted', 'accepted', 'arrived'].includes(activity.status)}
                              orderType={activity.orderType}
                              reason={activity.reason || activity.offlineReason}
                              style={{
                                ...mainPosition,
                                borderRadius: '0 4px 4px 0' // Nur rechts abgerundet
                              }}
                              onClick={(e) => handleActivityClick(e, activity)}
                            >
                              {activity.destination} (bis {endTime})
                              {activity.isDelayed && <WarningIcon sx={{ ml: 0.5, fontSize: '12px' }} />}
                            </ActivityBar>
                          </Tooltip>
                        </React.Fragment>
                      );
                    } else {
                      // Normale Aktivität ohne Anfahrtszeit
                      const position = calculateActivityPosition(activity);
                      
                      return (
                        <Tooltip 
                          key={activity.id}
                          title={normalTooltipContent}
                          arrow
                          placement="top"
                          enterDelay={200}
                        >
                          <ActivityBar
                            type={activity.type}
                            isDelayed={activity.isDelayed}
                            isPending={['scheduled', 'pre_accepted', 'accepted', 'arrived'].includes(activity.status)}
                            orderType={activity.orderType}
                            reason={activity.reason || activity.offlineReason}
                            style={position}
                            onClick={(e) => handleActivityClick(e, activity)}
                          >
                            {activity.destination} (bis {endTime})
                            {activity.isDelayed && <WarningIcon sx={{ ml: 0.5, fontSize: '12px' }} />}
                          </ActivityBar>
                        </Tooltip>
                      );
                    }
                  })}
                </VehicleTimelineRow>
              );
            })}
            
            <CurrentTimeLine style={{ left: `${currentTimePos}px` }} />
          </TimeGrid>
        </TimelineContainer>
        
        <Box>
          <FleetUtilizationRow>
            <UtilizationLabel>
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2">
                      Flottenauslastung
                    </Typography>
                    {(() => {
                      // Verwende globale Wartezeitdaten
                      const waitTimes = globalWaitTimes;
                      const nextUnavailable = globalNextUnavailable;
                      
                      // Intelligente Farbwahl basierend auf Wartezeit
                      const getWaitTimeColor = (waitTime) => {
                        if (waitTime.includes("nicht verfügbar") || 
                            waitTime.includes("Keine Fahrer verfügbar") || 
                            waitTime.includes("30+")) {
                          return "error.main"; // Rot für nicht verfügbar
                        } else if (waitTime.includes("nach Ausfallzeit") || waitTime.startsWith("2") || waitTime.startsWith("3")) {
                          return "warning.main"; // Orange für längere Wartezeiten
                        } else {
                          return "success.main"; // Grün für kurze Wartezeiten
                        }
                      };
                      
                      return (
                        <Box>
                          <Typography variant="caption" color={getWaitTimeColor(waitTimes.general)} sx={{ fontWeight: 'bold', display: 'block' }}>
                            Aktuelle Wartezeit: {waitTimes.general}
                          </Typography>
                          {nextUnavailable && (
                            <Typography variant="caption" color="error.main" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
                              Keine Fahrer verfügbar in {nextUnavailable.minutesUntil} Min (für {nextUnavailable.duration} Min)
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Großraumfahrzeuge (6 Sitze)
                    </Typography>
                    {(() => {
                      // Verwende globale Wartezeitdaten
                      const waitTimes = globalWaitTimes;
                      const isUnavailable = waitTimes.kombi === "nicht verfügbar" || waitTimes.kombi === "Keine Fahrer verfügbar";
                      return (
                        <Typography variant="caption" color={isUnavailable ? "error.main" : "success.main"} sx={{ fontSize: '0.65rem', display: 'block' }}>
                          Wartezeit: {waitTimes.kombi}
                        </Typography>
                      );
                    })()}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Minibus (8 Sitze)
                    </Typography>
                    {(() => {
                      // Verwende globale Wartezeitdaten
                      const waitTimes = globalWaitTimes;
                      const isUnavailable = waitTimes.minibus === "nicht verfügbar" || waitTimes.minibus === "Keine Fahrer verfügbar";
                      return (
                        <Typography variant="caption" color={isUnavailable ? "error.main" : "success.main"} sx={{ fontSize: '0.65rem', display: 'block' }}>
                          Wartezeit: {waitTimes.minibus}
                        </Typography>
                      );
                    })()}
                  </Box>
                </Box>
              </Box>
            </UtilizationLabel>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <UtilizationChart>
                <UtilizationTimeline style={{ width: `${memoizedTimeSlots.length * getSlotWidth()}px` }}>
                  {(() => {
                    const { segments } = availabilityTimeline;
                    
                    return segments.map((segment, index) => {
                      // Berechne Position in Pixeln basierend auf der gleichen Logik wie die Zeitslots
                      const slotWidth = getSlotWidth();
                      const leftPx = (segment.startMinute / 60) * slotWidth;
                      const widthPx = ((segment.endMinute - segment.startMinute) / 60) * slotWidth;
                      
                      const formatTimeRange = () => {
                        const startTime = segment.startTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        const endTime = segment.endTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        return `${startTime} - ${endTime}`;
                      };
                      
                      return (
                        <Tooltip
                          key={index}
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">
                                {formatTimeRange()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Status: {
                                  segment.status === 'normal' ? 'Verfügbar (normale Schichtzeit)' :
                                  segment.status === 'overtime' ? 'Verfügbar (nur Überstunden)' :
                                  segment.status === 'planned' ? 'Geplante Schichten' :
                                  'Nicht verfügbar'
                                }
                              </Typography>
                              {segment.available && (
                                <>
                                  <Typography variant="caption" display="block">
                                    Verfügbare Fahrer: {segment.availableCount} von {segment.totalWithDriver}
                                  </Typography>
                                  {segment.availableWithinShiftHours > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Innerhalb Schichtzeit: {segment.availableWithinShiftHours}
                                    </Typography>
                                  )}
                                  {segment.availableOverShiftHours > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Überstunden: {segment.availableOverShiftHours}
                                    </Typography>
                                  )}
                                </>
                              )}
                              {segment.status === 'planned' && segment.plannedShiftsCount && (
                                <Typography variant="caption" display="block">
                                  Geplante Schichten: {segment.plannedShiftsCount}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {segment.status === 'planned' ? 'Flottenauslastung geplant' : 
                                `Geschätzte Wartezeit: ${
                                  segment.status === 'normal' ? '5-10 Min' :
                                  segment.status === 'overtime' ? '15-20 Min (Überstunden)' :
                                  '20+ Min'
                                }`}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <UtilizationSegment
                            status={segment.status}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`
                            }}
                          />
                        </Tooltip>
                      );
                    });
                  })()}
                </UtilizationTimeline>
              </UtilizationChart>
              
              <UtilizationChart>
                <UtilizationTimeline style={{ width: `${memoizedTimeSlots.length * getSlotWidth()}px` }}>
                  {(() => {
                    const { segments } = kombiAvailability; // Type 2 = Kombi
                    
                    return segments.map((segment, index) => {
                      const slotWidth = getSlotWidth();
                      const leftPx = (segment.startMinute / 60) * slotWidth;
                      const widthPx = ((segment.endMinute - segment.startMinute) / 60) * slotWidth;
                      
                      const formatTimeRange = () => {
                        const startTime = segment.startTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        const endTime = segment.endTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        return `${startTime} - ${endTime}`;
                      };
                      
                      return (
                        <Tooltip
                          key={index}
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">
                                {formatTimeRange()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Status: {
                                  segment.status === 'normal' ? 'Verfügbar (normale Schichtzeit)' :
                                  segment.status === 'overtime' ? 'Verfügbar (nur Überstunden)' :
                                  segment.status === 'planned' ? 'Geplante Schichten' :
                                  'Nicht verfügbar'
                                }
                              </Typography>
                              {segment.available && (
                                <>
                                  <Typography variant="caption" display="block">
                                    Verfügbar für 6+ Personen: {segment.availableCount}
                                  </Typography>
                                  {segment.availableType2Count > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Kombis (6 Sitze): {segment.availableType2Count}
                                    </Typography>
                                  )}
                                  {segment.availableType3Count > 0 && (
                                    <Typography variant="caption" display="block">
                                      - Minibusse (8 Sitze): {segment.availableType3Count}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" display="block">
                                    Gesamt Fahrzeuge: {segment.totalVehicles}
                                  </Typography>
                                </>
                              )}
                              {segment.status === 'planned' && segment.plannedShiftsCount && (
                                <Typography variant="caption" display="block">
                                  Geplante Schichten: {segment.plannedShiftsCount}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {segment.status === 'planned' ? 'Flottenauslastung geplant' :
                                 segment.totalVehicles === 0 ? 'Keine passenden Fahrzeuge vorhanden' : 
                                 segment.available ? 'Fahrzeug für 6+ Personen verfügbar' : 'Kein Fahrzeug für 6+ Personen verfügbar'}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <UtilizationSegment
                            status={segment.status}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`
                            }}
                          />
                        </Tooltip>
                      );
                    });
                  })()}
                </UtilizationTimeline>
              </UtilizationChart>
              
              <UtilizationChart>
                <UtilizationTimeline style={{ width: `${memoizedTimeSlots.length * getSlotWidth()}px` }}>
                  {(() => {
                    const { segments } = minibusAvailability; // Type 3 = Kleinbus
                    
                    return segments.map((segment, index) => {
                      const slotWidth = getSlotWidth();
                      const leftPx = (segment.startMinute / 60) * slotWidth;
                      const widthPx = ((segment.endMinute - segment.startMinute) / 60) * slotWidth;
                      
                      const formatTimeRange = () => {
                        const startTime = segment.startTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        const endTime = segment.endTime.toLocaleTimeString('de-DE', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });
                        return `${startTime} - ${endTime}`;
                      };
                      
                      return (
                        <Tooltip
                          key={index}
                          title={
                            <Box>
                              <Typography variant="caption" fontWeight="bold">
                                {formatTimeRange()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Status: {
                                  segment.status === 'normal' ? 'Verfügbar (normale Schichtzeit)' :
                                  segment.status === 'overtime' ? 'Verfügbar (nur Überstunden)' :
                                  segment.status === 'planned' ? 'Geplante Schichten' :
                                  'Nicht verfügbar'
                                }
                              </Typography>
                              {segment.available && (
                                <>
                                  <Typography variant="caption" display="block">
                                    Verfügbare Minibusse: {segment.availableCount} von {segment.totalWithDriver}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    Gesamt Minibusse: {segment.totalVehicles}
                                  </Typography>
                                </>
                              )}
                              {segment.status === 'planned' && segment.plannedShiftsCount && (
                                <Typography variant="caption" display="block">
                                  Geplante Schichten: {segment.plannedShiftsCount}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {segment.status === 'planned' ? 'Flottenauslastung geplant' :
                                 segment.totalVehicles === 0 ? 'Keine Minibusse vorhanden' : 
                                 segment.available ? 'Minibus verfügbar' : 'Kein Minibus verfügbar'}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <UtilizationSegment
                            status={segment.status}
                            style={{
                              left: `${leftPx}px`,
                              width: `${widthPx}px`
                            }}
                          />
                        </Tooltip>
                      );
                    });
                  })()}
                </UtilizationTimeline>
              </UtilizationChart>
            </Box>
          </FleetUtilizationRow>
          
          <FleetUtilizationRow sx={{ minHeight: '50px', height: '50px' }}>
            <UtilizationLabel sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle2">
                Offene Vorbestellungen
              </Typography>
            </UtilizationLabel>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative', height: '100%' }}>
              <UtilizationTimeline style={{ 
                width: `${memoizedTimeSlots.length * getSlotWidth()}px`, 
                height: '20px',
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)'
              }}>
                {calculateScheduledRidesTimeline().map((ride, index) => {
                  const formatTimeRange = () => {
                    const startTime = ride.startTime.toLocaleTimeString('de-DE', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    });
                    const endTime = ride.endTime.toLocaleTimeString('de-DE', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    });
                    return `${startTime} - ${endTime}`;
                  };
                  
                  return (
                    <Tooltip
                      key={ride.id}
                      title={
                        <Box>
                          <Typography variant="caption" fontWeight="bold">
                            {formatTimeRange()}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Ziel: {ride.destination}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Kunde: {ride.customerName}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Fahrzeugtyp: {ride.vehicleType === 1 ? 'PKW' : ride.vehicleType === 2 ? 'Kombi' : 'Bus'}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Status: {ride.status === 'scheduled' ? 'Vorgebucht' : 'Offen'}
                          </Typography>
                          {ride.overlaps && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'warning.main' }}>
                              ⚠️ Überlappung mit anderen Fahrten
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <ScheduledRideBar
                        color={ride.color}
                        style={{
                          left: `${ride.left}px`,
                          width: `${ride.width}px`
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </UtilizationTimeline>
            </Box>
          </FleetUtilizationRow>
          
        </Box>
      </GanttContainer>
    );
  };

  // Render Week Overview
  const renderWeekView = () => {
    const weekData = getWeekOverviewData();
    const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    
    return (
      <WeekOverviewContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Wochenübersicht</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handlePrevious}>
              <PreviousIcon />
            </IconButton>
            <Typography variant="subtitle1">
              {currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
            </Typography>
            <IconButton onClick={handleNext}>
              <NextIcon />
            </IconButton>
          </Box>
        </Box>
        
        <WeekGrid>
          <WeekCell isHeader>Fahrzeug</WeekCell>
          {weekDays.map((day) => (
            <WeekCell key={day} isHeader>{day}</WeekCell>
          ))}
          
          {weekData.map(({ vehicle, week }) => (
            <React.Fragment key={vehicle.id}>
              <WeekCell>
                <Typography variant="body2" fontWeight="medium">
                  {vehicle.model || vehicle.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {vehicle.licensePlate}
                </Typography>
              </WeekCell>
              {week.map(({ day, utilization, hours }, index) => (
                <WeekCell 
                  key={index}
                  utilization={utilization}
                  onClick={() => {
                    setCurrentDate(day);
                    setCurrentView('gantt');
                  }}
                >
                  <Typography variant="body2" fontWeight="medium">
                    {hours}h
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {Math.round(utilization * 100)}%
                  </Typography>
                </WeekCell>
              ))}
            </React.Fragment>
          ))}
        </WeekGrid>
      </WeekOverviewContainer>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Taxi-Kalender"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Schichtplanung', path: '/schichten' }
        ]}
        secondaryButton={{
          label: 'Schichtplaner',
          icon: <ListIcon />,
          path: '/schichtplaner'
        }}
        actionButton={{
          label: 'Neue Fahrt',
          icon: <AddIcon />,
          path: '/fahrten/neu'
        }}
      />
      
      {currentView === 'gantt' ? renderGanttView() : renderWeekView()}
      
      {/* Activity Detail Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        {selectedActivity && (
          <>
            <MenuItem onClick={handleCloseMenu}>
              <ViewIcon sx={{ mr: 1 }} />
              Details anzeigen
            </MenuItem>
            {selectedActivity.type === 'ride' && (
              <MenuItem onClick={handleCloseMenu}>
                <EditIcon sx={{ mr: 1 }} />
                Fahrt bearbeiten
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </Box>
  );
};

export default ShiftCalendar;
